/**
 * Krabbellijn: de doorfluistertelefoon, maar met krabbels.
 *
 * Iemand begint met een zin. De volgende ziet alleen die zin en tekent hem. De
 * derde ziet alleen die tekening en schrijft op wat hij denkt dat het is. Zo gaat
 * het door, en aan het eind lees je de eerste zin naast de laatste. Daar zit de
 * grap: hij is onderweg door vijf handen gegaan en er is niks van over.
 *
 * ---- waarom een keten en geen ronde ------------------------------------
 *
 * In het echte spel zit iedereen tegelijk achter een scherm en gaat elke ronde
 * het blaadje door. Dat werkt hier niet: de paal is geen avondje maar een dag, en
 * er zijn zelden vier mensen op hetzelfde moment binnen. Daarom loopt een keten
 * hier door de tijd. Je doet één stap, en wie er daarna langskomt doet de
 * volgende. Een keten die vanmorgen begon is 's middags klaar, en dan mag
 * iedereen hem teruglezen.
 *
 * ---- de stappen liggen los op de server ---------------------------------
 *
 * Elke stap is zijn eigen regel, met de keten waar hij bij hoort en zijn nummer
 * erin. Dat is met opzet: als twee mensen tegelijk stap drie insturen, dan komen
 * er twee regels met nummer drie en staan ze er allebei. Zou de hele keten in één
 * regel staan, dan overschreef de tweede de eerste en was er een tekening weg.
 * Bij dubbele nummers geldt de oudste, want die was er eerst.
 */

/** Zoveel stappen heeft een keten. Oneven, dus hij eindigt op een zin. */
export const STAPPEN = 5;

/**
 * Zoveel tijd heb je voor jouw stap: een kwartier.
 *
 * De klok staat niet in je browser maar op de regel: wie een stap doorgeeft schrijft
 * erbij tot wanneer de volgende de tijd heeft. Dat is het verschil tussen een timer
 * en een afspraak. Sluit je de app, dan loopt hij door. Open je hem op je telefoon,
 * dan zie je dezelfde tijd. En niemand hoeft iets op te ruimen als jij verdwijnt:
 * zodra het kwartier om is ziet iedereen dat de beurt vrij is.
 */
export const BEURT_MS = 15 * 60 * 1000;

/**
 * ---- twee tempo's ----------------------------------------------------
 *
 * Rustig is een kwartier per stap: dan loopt een keten door de dag en doe je
 * hem tussendoor. Samen is negentig seconden tekenen en vijfenveertig seconden
 * raden: dan spreek je met elkaar af, zit iedereen tegelijk klaar en is een hele
 * keten in vijf minuten rond. Dat zijn twee verschillende spelletjes en ze zijn
 * allebei leuk, dus kiest degene die de keten begint welke het wordt.
 *
 * Het tempo staat niet apart op de server. Het volgt uit de regel zelf: hoeveel
 * tijd de vorige speler de volgende gaf. Dat scheelt een veld en het kan nooit
 * uit de pas lopen met de klok die er echt op staat.
 */
export const SNEL_KRABBEL_MS = 90 * 1000;
export const SNEL_ZIN_MS = 45 * 1000;

/** De grens: alles wat korter is dan dit hoort bij het snelle tempo. */
const SNEL_GRENS_MS = 5 * 60 * 1000;

/** Speelt deze keten op tempo? Afgeleid uit wat de vorige speler meegaf. */
export function isSnel(keten) {
  if (!keten || !keten.laatste || !keten.tot) return false;
  const gezet = Date.parse(String(keten.laatste.created || '').replace(' ', 'T'));
  if (isNaN(gezet)) return false;
  return keten.tot - gezet < SNEL_GRENS_MS;
}

/** Hoeveel tijd de volgende stap krijgt, in milliseconden. */
export function beurtDuur(snel, volgendeNr) {
  if (!snel) return BEURT_MS;
  return soortVan(volgendeNr) === 'krabbel' ? SNEL_KRABBEL_MS : SNEL_ZIN_MS;
}

/** Zoveel tekens mag een zin zijn. Eén regel, geen verhaal. */
export const ZIN_MAX = 60;

/** Een krabbel is dertig bij dertig… nee: tweeëndertig bij tweeëndertig. */
export const VAK = 32;

/** Een leeg vel: even veel tekens als vakjes, allemaal doorzichtig. */
export const LEEG = '.'.repeat(VAK * VAK);

/**
 * Wat er op stap `nr` moet gebeuren.
 *
 * Even stappen zijn zinnen, oneven stappen tekeningen. Stap nul is dus de zin
 * waarmee iemand begint, en de laatste stap is weer een zin.
 */
export const soortVan = (nr) => (nr % 2 === 0 ? 'zin' : 'krabbel');

/** Klopt deze zin? Niet leeg, en niet langer dan één regel. */
export const zinOk = (t) => {
  const s = String(t == null ? '' : t).trim();
  return s.length > 0 && s.length <= ZIN_MAX;
};

/** Staat er iets op dit vel? Een keten met een leeg vel erin is geen keten. */
export const krabbelOk = (p) => typeof p === 'string' && p.length === VAK * VAK && /[0-9a-f]/.test(p);

/**
 * Is dit een ketennummer waar we iets mee doen?
 *
 * Alleen kleine letters en cijfers. Het nummer gaat later in een filter naar de
 * server, en zo'n regel kan iedereen met een account aanmaken: een nummer met een
 * aanhalingsteken erin zou die filter openbreken. Wat er niet uitziet als een
 * nummer van ons doet gewoon niet mee.
 */
export const ketenIdOk = (id) => /^[a-z0-9]{1,32}$/.test(String(id || ''));

/**
 * De losse stappen van de server tot ketens smeden.
 *
 * Levert per keten: de stappen op nummer, of hij klaar is, wie er al aan mee
 * deden en wie de laatste was. Dubbele nummers vallen weg: de oudste blijft.
 * Gaten ook: staat stap twee er nog niet, dan houdt de keten daar op, want stap
 * drie hoort niet bij een tekening die nog niet bestaat.
 */
export function ketens(rijen) {
  const perKeten = new Map();
  (rijen || []).forEach((r) => {
    const id = String(r.keten || '');
    const nr = Number(r.nr);
    if (!ketenIdOk(id) || !Number.isInteger(nr) || nr < 0 || nr >= STAPPEN) return;
    if (!perKeten.has(id)) perKeten.set(id, new Map());
    const op = perKeten.get(id);
    const er = op.get(nr);
    // De oudste wint. Zonder tijd op de regel wint wie er al lag.
    if (!er || String(r.created || '') < String(er.created || '')) op.set(nr, r);
  });
  const uit = [];
  perKeten.forEach((op, id) => {
    const stappen = [];
    for (let nr = 0; nr < STAPPEN; nr++) {
      const stap = op.get(nr);
      if (!stap) break;
      stappen.push(stap);
    }
    if (!stappen.length) return;
    const laatste = stappen[stappen.length - 1];
    uit.push({
      id,
      stappen,
      lengte: stappen.length,
      klaar: stappen.length >= STAPPEN,
      // Waar de keten op wacht, en van wie de vorige stap was.
      volgende: stappen.length,
      laatste,
      mee: stappen.map((s) => String(s.authorId || '')),
      begon: String(stappen[0].created || ''),
      // Aan wie de laatste stap is doorgegeven, en tot wanneer diegene de tijd heeft.
      // Het heet naarId en niet naar, want het is precies hetzelfde soort waarde
      // als authorId: een accountId uit paal_accounts. Een veld dat "naar" heet
      // laat open of er een naam of een nummer in staat, en dat is één keer
      // verkeerd lezen waard. Hoe iemand heet wordt opgezocht; zie ketNaam(). Zou
      // de naam hier meegeschreven worden, dan blijft in een oud verhaal de oude
      // naam staan terwijl diegene zich intussen anders noemt.
      naarId: String(laatste.naarId || ''),
      tot: Number(laatste.tot) || 0
    });
  });
  // Nieuwste keten bovenaan; de oudste onderaan wacht al het langst.
  uit.sort((a, b) => (a.begon < b.begon ? 1 : a.begon > b.begon ? -1 : 0));
  return uit;
}

/**
 * Ligt deze keten stil?
 *
 * Een beurt is aan één persoon gericht en heeft een einde. Is dat kwartier om en
 * heeft die persoon niets ingestuurd, dan ligt de keten stil en mag een ander hem
 * oppakken. Zo blijft er niets hangen aan iemand die die dag niet meer inlogt,
 * zonder dat er op de server iets hoeft te draaien dat opruimt.
 */
export const verlopen = (keten, nu) => !keten.klaar && (!keten.tot || keten.tot <= (nu || 0));

/**
 * De keten waar jij nú aan de beurt bent.
 *
 * Dat is er hoogstens één: de keten die aan jou is doorgegeven en waarvan het
 * kwartier nog loopt. Zijn er meer, dan telt degene die het eerst afloopt, want
 * die is het meest haast.
 */
export function jouwBeurt(lijst, mijnId, nu) {
  const ik = String(mijnId || '');
  if (!ik) return null;
  const mijn = (lijst || []).filter((k) => !k.klaar && k.naarId === ik && !verlopen(k, nu));
  if (!mijn.length) return null;
  return mijn.slice().sort((a, b) => a.tot - b.tot)[0];
}

/**
 * De ketens die stilliggen en die jij mag oppakken.
 *
 * Niet die van jezelf: je zou je eigen zin tekenen, en dan is het geen spel meer.
 * De langst stilliggende bovenaan, want die wacht al het langst op iemand.
 */
export function vrijeBeurten(lijst, mijnId, nu) {
  const ik = String(mijnId || '');
  return (lijst || [])
    .filter((k) => verlopen(k, nu) && String(k.laatste.authorId || '') !== ik)
    .sort((a, b) => (a.tot || 0) - (b.tot || 0));
}

/** Hoeveel tijd je nog hebt, in milliseconden, nooit negatief. */
export const resterend = (keten, nu) => Math.max(0, (keten && keten.tot ? keten.tot : 0) - (nu || 0));

/**
 * Een nieuw ketennummer.
 *
 * Vijftien tekens, net als de nummers die de server zelf uitdeelt. Het hoeft
 * niet geheim te zijn, alleen uniek genoeg dat twee mensen die op hetzelfde
 * moment beginnen niet in elkaars keten terechtkomen.
 */
export function nieuwKetenId(rand) {
  const r = rand || Math.random;
  const tekens = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let uit = '';
  for (let i = 0; i < 15; i++) uit += tekens[Math.floor(r() * tekens.length) % tekens.length];
  return uit;
}

/**
 * Alle vakjes tussen twee punten, dat ene punt zelf erbij.
 *
 * Een muis stuurt niet elk vakje langs. Beweeg je snel, dan springt hij van
 * (3,3) naar (9,7) en zonder deze lijn zou je streek een rij losse stippen zijn.
 * Dit is de tekenlijn van Bresenham: alleen optellen en aftrekken, geen deling,
 * dus geen afrondingsgedoe op een raster van tweeëndertig.
 */
export function lijn(x0, y0, x1, y1) {
  const uit = [];
  let x = x0, y = y0;
  const dx = Math.abs(x1 - x0), dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let fout = dx - dy;
  for (let n = 0; n < 4096; n++) {
    uit.push([x, y]);
    if (x === x1 && y === y1) break;
    const f2 = 2 * fout;
    if (f2 > -dy) { fout -= dy; x += sx; }
    if (f2 < dx) { fout += dx; y += sy; }
  }
  return uit;
}

/**
 * Een vakje verven en het vel teruggeven, met een kwast van `dik` bij `dik`.
 *
 * Het vel is een tekst van duizendvierentwintig tekens: één teken per vakje, een
 * cijfer uit het palet of een punt voor niks. Dat is precies hoe de avatars al
 * bewaard worden, dus het past in een tekstveld en pixUrl() maakt er zonder
 * omweg een plaatje van.
 *
 * Bij een even kwast ligt hij een half vakje naar linksboven, net als bij de
 * kwast op het grote tekendoek.
 */
export function verf(pix, x, y, teken, dik) {
  const vel = (typeof pix === 'string' && pix.length === VAK * VAK ? pix : LEEG).split('');
  const n = Math.max(1, Math.min(8, Number(dik) || 1));
  const off = Math.floor((n - 1) / 2);
  for (let dy = 0; dy < n; dy++) {
    for (let dx = 0; dx < n; dx++) {
      const px = x - off + dx, py = y - off + dy;
      if (px < 0 || py < 0 || px >= VAK || py >= VAK) continue;
      vel[py * VAK + px] = teken;
    }
  }
  return vel.join('');
}

/**
 * ---- het kader ----------------------------------------------------------
 *
 * Hetzelfde gereedschap als in de avatar-tekenaar: je wijst een vlak aan, en dan
 * kun je het verslepen of in één keer vullen. De regels staan hier omdat ze pure
 * rekenarij zijn en zo na te rekenen zijn zonder browser.
 */

/** Twee aangewezen vakjes worden één rechthoek, netjes binnen het veld. */
export function rechthoek(a, b) {
  const klem = (n) => Math.max(0, Math.min(VAK - 1, Math.round(n)));
  const x0 = klem(Math.min(a.x, b.x)), y0 = klem(Math.min(a.y, b.y));
  const x1 = klem(Math.max(a.x, b.x)), y1 = klem(Math.max(a.y, b.y));
  return { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

/** Ligt dit vakje binnen de rechthoek? */
export const inVak = (r, x, y) => !!r && x >= r.x && y >= r.y && x < r.x + r.w && y < r.y + r.h;

/** De hele rechthoek in één keer vullen; met een punt gumt hij hem leeg. */
export function vul(pix, r, teken) {
  const vel = (typeof pix === 'string' && pix.length === VAK * VAK ? pix : LEEG).split('');
  if (!r) return vel.join('');
  for (let y = r.y; y < r.y + r.h; y++) {
    for (let x = r.x; x < r.x + r.w; x++) {
      if (x < 0 || y < 0 || x >= VAK || y >= VAK) continue;
      vel[y * VAK + x] = teken;
    }
  }
  return vel.join('');
}

/**
 * De inhoud van de rechthoek naar (nx, ny) verplaatsen.
 *
 * Eerst uitknippen, dan de plek waar hij weg gaat leegmaken, dan neerzetten. In
 * die volgorde, want anders veegt het leegmaken de helft van wat je net neerzette
 * weer weg zodra oud en nieuw over elkaar liggen.
 */
export function verplaats(pix, r, nx, ny) {
  const bron = (typeof pix === 'string' && pix.length === VAK * VAK ? pix : LEEG);
  if (!r) return bron;
  const stuk = [];
  for (let y = 0; y < r.h; y++) {
    for (let x = 0; x < r.w; x++) {
      const px = r.x + x, py = r.y + y;
      stuk.push(px >= 0 && py >= 0 && px < VAK && py < VAK ? bron[py * VAK + px] : '.');
    }
  }
  const vel = vul(bron, r, '.').split('');
  for (let y = 0; y < r.h; y++) {
    for (let x = 0; x < r.w; x++) {
      const px = nx + x, py = ny + y;
      if (px < 0 || py < 0 || px >= VAK || py >= VAK) continue;
      vel[py * VAK + px] = stuk[y * r.w + x];
    }
  }
  return vel.join('');
}
